import React, { CSSProperties, ReactNode, useState } from 'react';
import { Trans } from '@lingui/react/macro';

import Badge from '@/design_system/Badge';
import Tooltip from '@/design_system/Tooltip';
import IconAnalysis from '@/icons/Analysis.svg';
import { Medium } from '@/models/medium';
import { createBEMClasses } from '@/utils/classname';

import PhotoCard, { PhotoCardProps } from './PhotoCard';
import PhotoDialog from './PhotoDialog';

import './PhotoCardGrid.css';

const { block } = createBEMClasses('photo-card-grid');

type PhotoCardGridProps = {
  media: (Partial<Medium> & { id: string; url: string; customIcon?: ReactNode })[];
  loadingUploads?: { name: string }[];
  onDelete?: (id: string) => void;
  size?: PhotoCardProps['size'];
  variant?: 'default' | 'brand';
  input?: React.ReactNode;
  imgStyle?: CSSProperties;
  persistentDeleteIcon?: boolean;
};

/**
 *
 * Handle the display of uploaded and loading photos under the same format in a grid
 */
const PhotoCardGrid = ({
  media,
  loadingUploads,
  onDelete,
  size = 'large',
  variant = 'default',
  input,
  imgStyle,
  persistentDeleteIcon,
}: PhotoCardGridProps) => {
  const [openIndex, setOpenIndex] = useState<number | undefined>();

  const nonDeletedMedia = media.filter((medium) => !medium.deletedAt);
  const openMedium = openIndex !== undefined ? nonDeletedMedia?.[openIndex] : undefined;

  return (
    <div className={block({ size, 'with-input': !!input })}>
      {media.map((medium) => (
        <PhotoCard
          size={size}
          key={medium.id}
          name={medium.originalPath ?? ''}
          alt={medium.originalPath ?? ''}
          url={medium.url}
          onDelete={onDelete && !medium.undeletable ? () => onDelete(medium.id) : undefined}
          onZoom={() => setOpenIndex(nonDeletedMedia.findIndex(({ id }) => id === medium.id))}
          variant={variant}
          customIcon={
            medium.customIcon ?? iconForType.find(({ type }) => type === medium.type)?.customIcon
          }
          imgStyle={imgStyle}
          persistentDeleteIcon={persistentDeleteIcon}
          showDeletedPlaceholder={!!medium.deletedAt}
        />
      ))}

      {loadingUploads?.map((loadingFile, index) => (
        <PhotoCard key={loadingFile.name + index} size={size} name={loadingFile.name} loading />
      ))}

      {openIndex !== undefined && !!openMedium && (
        <PhotoDialog
          url={openMedium.url}
          alt={openMedium.originalPath ?? ''}
          isOpen={openIndex !== undefined}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setOpenIndex(undefined);
            }
          }}
          onPrevious={
            nonDeletedMedia.length > 1
              ? () => {
                  if (openIndex > 0) {
                    setOpenIndex(openIndex - 1);
                  }
                }
              : undefined
          }
          onNext={
            nonDeletedMedia.length > 1
              ? () => {
                  if (openIndex < nonDeletedMedia.length - 1) {
                    setOpenIndex(openIndex + 1);
                  }
                }
              : undefined
          }
          disablePrevious={openIndex === 0}
          disableNext={openIndex === nonDeletedMedia.length - 1}
          variant={variant}
          customIcon={iconForType.find(({ type }) => type === openMedium.type)?.customIcon}
        />
      )}

      {input}
    </div>
  );
};

const RequalificationDefectPhotoIcon = () => (
  <Tooltip content={<Trans id="requalification.photo.tooltip">Taken by the workshop</Trans>}>
    <Badge color="secondary" size="large" iconOnly onClick={() => {}}>
      <IconAnalysis />
    </Badge>
  </Tooltip>
);

const iconForType = [
  { type: 'requalification-defect-photo', customIcon: <RequalificationDefectPhotoIcon /> },
];

export default PhotoCardGrid;
